import type { AxiosResponse } from "axios";

export type StepLabel = "overview" | "income" | "expenses" | "complete";
export type PaymentStatusOptions = "Current" | "Behind" | "Collections" | "";
export type Frequencies = 52 | 26 | 24 | 12 | 2 | 1;
export type Languages = "en" | "es" | "en-ca" | "fr-ca";
export type Countries = "CA" | "US";
export enum Tenant {
	CONSOLIDATED_CREDIT = "consolidated-credit",
	CONSOLIDATED_CREDIT_CANADA = "consolidated-credit-canada",
	NEW_LEAF = "new-leaf",
}

export interface ChatMessage {
	Message?: {
		Text?: Message;
		User?: boolean;
	};
}

export type Message = { text?: string };

export interface HandshakeParameters {
	lead_id: string;
	applicant_channel?: string; // "thankyou" | "sms" | ""
	country: Countries;
	tenant: Tenant;
	tenant_display_name: string;
	vicType: string;
}

export interface ApplicantParams {
	applicant_first_name?: string;
	applicant_last_name?: string;
	applicant_cell_phone?: string;
	applicant_zip_code?: string;
	applicant_trusted_form_url?: string;
}

export interface AssistanceReasonParams {
	assistance_reason?: string;
	assistance_reason_options?: string;
}

export interface DebtAmountParams {
	debt_amount?: string | number;
}

export interface DebtTypesParams {
	debt_types?: string[];
}

export interface HasIncomeParams {
	has_income?: boolean;
}

export interface PaymentStatusParams {
	payment_status?: PaymentStatusOptions;
}

export interface HouseholdParams {
	applicant_household_size?: string | number;
}
export type SourceOptions = { label: string; value: Sources };

export type Sources =
	| "Applicant Primary Income"
	| "Applicant Secondary Income"
	| "Applicant Alimony/Child Support"
	| "Applicant Social Security"
	| "Applicant Pension"
	| "Applicant Disability"
	| "Applicant Unemployment"
	| "Applicant Rental Income"
	| "Applicant Other Income"
	| "";

export type FrequencyOptions = { label: string; value: Frequencies };

export interface IncomeParams {
	applicant_income?: BudgetElement[];
}

export interface ExpensesParams {
	applicant_expenses?: BudgetElement[];
}

export interface BudgetElement {
	name?: string;
	category?: string;
	subCategory?: SubCategories;
	frequency?: Frequencies;
	amount?: number | string;
	type?: "Fixed" | "Variable";
}

export interface CableInternetPhoneParams {
	Internet?: string;
	Cable?: string;
	Phone?: string;
	Cellular?: string;
	["Bundle TV/Cable/Internet"]?: string;
}

export interface VehicleParams {
	["Auto/Boat/Bike Payment 1"]?: string;
	["Auto/Boat/Bike Payment 2"]?: string;
	["Auto/Boat/Bike Payment 3"]?: string;
	["Car Insurance"]?: string;
	["Repairs/Maintenance"]?: string;
	Gas?: string;
	["Parking/Tolls"]?: string;
}

export interface ChildCareParams {
	["Child Care"]?: string;
	["Child Support/Alimony"]?: string;
}

export interface FoodParams {
	Grocery?: string;
	["Lunches/Eating Out"]?: string;
}

export interface HealthParams {
	["Health Insurance"]?: string;
	["Life Insurance"]?: string;
	["Doctor Bills"]?: string;
	["Prescription/Medication"]?: string;
}

export interface MortgageParams {
	["First Mortgage"]?: string;
	["Second Mortgage"]?: string;
	["Association Fees"]?: string;
	["Real Estate Taxes"]?: string;
	["Home Owners Insurance"]?: string;
	["Home Equity Loan"]?: string;
}

export interface RentParams {
	Rent?: string;
	["Renters Insurance"]?: string;
	["Renter's Insurance"]?: string;
}

// for the form before the data gets properly formatted
export interface OtherExpense {
	type?: keyof OtherExpensesParams;
	amount?: string;
	frequency?: number;
}

export interface OtherExpensesForm {
	other_expenses: OtherExpense[];
}

// for the dropdown form
export interface OtherExpensesTypeOption {
	label: string;
	value: keyof OtherExpensesParams;
}

// the structure the data gets formatted to onSubmit
export interface OtherExpensesParams {
	Clothing?: string;
	["Hair Care/Nails/Barber"]?: string;
	Pets?: string;
	["Cigarettes"]?: string;
	["Educational/Professional"]?: string;
	["Church/Tithe/Charity"]?: string;
	["Cleaning/Laundry"]?: string;
	["Health Club Membership"]?: string;
	["Home Repairs/Maintenance"]?: string;
	["Student Loan"]?: string;
	["Total Recurring Credit Card Payments"]?: string;
	["Total Recurring Personal Loan Payments"]?: string;
	Miscellaneous?: string;
	Savings?: string;
	["Other Loan"]?: string;
	["CRA/Tax Payments"]?: string;
	["Alimony"]?: string;
	["Entertainment"]?: string;
}

export interface TransportationParams {
	["Bus Fare"]?: string;
	["Other Transportation Fees"]?: string;
}

export interface UtilitiesParams {
	Electricity?: string;
	Water?: string;
	["Gas/Oil/Heat"]?: string;
}

export interface CallbackParams {
	applicant_callback_time?: string | Date;
}

export interface VicParams {
	RedirectThankYouPageUrl?: string | URL;
}

export type SubCategories =
	| keyof CableInternetPhoneParams
	| keyof UtilitiesParams
	| keyof FoodParams
	| keyof MortgageParams
	| keyof RentParams
	| keyof VehicleParams
	| keyof TransportationParams
	| keyof HealthParams
	| keyof ChildCareParams
	| keyof OtherExpensesParams
	| Sources;

export interface FormExpenses
	extends CableInternetPhoneParams,
		UtilitiesParams,
		FoodParams,
		MortgageParams,
		RentParams,
		VehicleParams,
		TransportationParams,
		HealthParams,
		ChildCareParams,
		OtherExpensesParams {}

export interface FormExpensesWithFrequency {
	amount: FormExpenses;
	frequency: Frequencies;
}

export interface TrustedFormParams {
	applicant_trusted_form_url?: string;
}

export interface StatusParams {
	applicant_qualified_status?: "Qualified" | "Not Qualified" | "Pending";
	applicant_finalized?: boolean;
	applicant_percent_complete?: number;
	applicant_last_page_collected?: string;
	applicant_form_status?:
		| "In Progress"
		| "Complete"
		| "Abandoned"
		| "Pending"
		| "Locked";
	applicant_page_position?: number;
}

export interface VicParams {
	vic?: {
		consent?: {
			collectPII?: {
				attributes?: {
					expiration?: string;
					consentDate?: string;
				};
				consumer?: {
					attributes?: {
						inVerify?: boolean;
						householdSize?: number;
						phone?: {
							number?: string;
							flags?: string[];
						};
						country?: string;
						zip?: string;
					};
					firstName?: string;
					lastName?: string;
					id?: string;
				};
			};
			ventureTech?: {
				attributes?: {
					AllowFormEdit?: boolean;
					callbackTime?: string;
					percentComplete?: number;
					trustedFormUrl?: string;
					formStatus?: StatusParams["applicant_form_status"];
					finalized?: boolean;
					VTResponse: {
						RedirectThankYouPageUrl: string;
						AllowFormEdit: boolean;
						Language: "en" | "es" | "fr";
						ConsentNeeded: boolean;
					};
				};
				consumer: {
					attributes?: {
						assistanceReason?: string;
						assistanceReasonOptions?: string;
						debtAmount?: number;
						debtTypes?: string[];
						hasIncome?: boolean;
						debtStatus?: PaymentStatusOptions;
						email?: {
							address?: string;
						};
						phone?: {
							number?: string;
						};
						zip: string;
					};
					firstName?: string;
					id?: string;
					lastName?: string;
				};
			};
		};
		currentBudget: {
			aggregates: {
				totalMonthlyChildrenExpenses?: number;
				totalMonthlyExpenses?: number;
				totalMonthlyFixedExpenses?: number;
				totalMonthlyFixedIncome?: number;
				totalMonthlyFoodExpenses?: number;
				totalMonthlyHousingExpenses?: number;
				totalMonthlyIncome?: number;
				totalMonthlyInsuranceExpenses?: number;
				totalMonthlyMedicalExpenses?: number;
				totalMonthlyPersonalExpenses?: number;
				totalMonthlyTransportationExpenses?: number;
				totalMonthlyUtilitiesExpenses?: number;
				totalMonthlyVariableExpenses?: number;
				totalMonthlyVariableIncome?: number;
			};
			elements: BudgetElement[];
		};
		leadID?: string;
	};
}

export interface SessionParams
	extends ApplicantParams,
		AssistanceReasonParams,
		DebtAmountParams,
		DebtTypesParams,
		HasIncomeParams,
		PaymentStatusParams,
		IncomeParams,
		ExpensesParams,
		CableInternetPhoneParams,
		VehicleParams,
		ChildCareParams,
		FoodParams,
		HealthParams,
		HouseholdParams,
		MortgageParams,
		RentParams,
		OtherExpensesParams,
		TransportationParams,
		UtilitiesParams,
		CallbackParams,
		TrustedFormParams,
		StatusParams,
		VicParams {
	country?: string;
}

export interface DFResponseMessages {
	response_messages: any[];
}

export type ResponseJSON = {
	Query: { Text: string };
	parameters: SessionParams;
	intent_detection_confidence: number;
} & DFResponseMessages;

export interface PostIntentPayload {
	sessionId: string;
	queryInput: {
		languageCode: string;
		input: {
			text: {
				text?: string;
			};
		};
	};
	queryParams?: {
		parameters: any;
	};
}

export type PostIntentResponse = AxiosResponse<ResponseJSON>;

export interface SkippableRoutes {
	applicant?: boolean;
	debtAmount?: boolean;
	paymentStatus?: boolean;
}
